import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Link } from 'gatsby';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

import Layout from '../components/layout';
import Seo from '../components/seo';
import Testimonial from '../components/Testimonial';
import Table from '../components/Table';
import Feature from '../components/Feature';
import VideoSlider from '../components/VideoSlider';
import FeaturedJobs from '../components/FeaturedJobs';
import useMediaQuery from '../hooks/useMediaQuery';

gsap.registerPlugin(ScrollTrigger);

const Index = () => {
  const heroContainer = useRef();
  const heroContainerBlock = useRef();
  const secondSection = useRef();
  const heroButtonResume = useRef();
  const heroSquare = useRef();
  const heroContainerTarget = useRef();
  const heroVideo = useRef();
  const buttonHired = useRef();
  const vidRef = useRef();
  const howItWorks = useRef();

  const isDesktop = useMediaQuery('(min-width: 991px)');
  const isMobile = useMediaQuery('(max-width: 990px)');

  const [unmute, setUnmute] = useState(true);

  const tl = gsap.timeline();

  const handleUnmuteVideo = useCallback(() => {
    vidRef.current.muted = !vidRef.current.muted;
    setUnmute(vidRef.current.muted);
  }, []);

  const handleWindowScroll = useCallback(() => {
    // set height of the svg path as constant
    const svg = document.getElementById('svgPath');
    const length = svg.getTotalLength();
    // start positioning of svg drawing
    svg.style.strokeDasharray = length;
    // hide svg before scrolling starts
    svg.style.strokeDashoffset = length;

    let scrollpercent =
      (heroContainerBlock.current?.scrollTop +
        document.documentElement.scrollTop) /
      (heroContainerBlock.current?.scrollHeight -
        document.documentElement.clientHeight);

    if (scrollpercent < -1 || scrollpercent > 0) {
      scrollpercent = -1;
    }
    const draw = length * scrollpercent;
    // Reverse the drawing when scroll upwards
    svg.style.strokeDashoffset = length + draw;
  }, []);

  const handleHeroVideoAutoMute = useCallback(() => {
    if (document.documentElement.scrollTop >= howItWorks.current?.offsetTop) {
      vidRef.current.muted = true;
      setUnmute(vidRef.current.muted);
    }
  }, []);

  useEffect(() => {
    if (isDesktop) {
      tl.fromTo(
        heroVideo.current,
        { scale: 1 },
        {
          scrollTrigger: {
            trigger: heroContainer.current,
            start: '50% 50%',
            endTrigger: secondSection.current,
            end: '350 50%',
            pin: heroVideo.current,
            scrub: true,
            pinSpacing: false,
            // markers: true,
          },
          scale: 1.16,
          duration: 2,
          ease: 'power2.out',
        },
      );
    }

    if (isMobile) {
      tl.fromTo(
        heroVideo.current,
        { scale: 1 },
        {
          scrollTrigger: {
            trigger: heroContainer.current,
            start: '50% 50%',
            endTrigger: secondSection.current,
            end: '370 50%',
            pin: heroVideo.current,
            scrub: true,
            pinSpacing: false,
            // markers: true,
          },
          scale: 1.16,
          duration: 2,
          ease: 'power2.out',
        },
      );
    }

    tl.fromTo(
      heroSquare.current,
      { autoAlpha: 1, y: 0 },
      {
        scrollTrigger: {
          trigger: heroContainer.current,
          start: '50% 40%',
          end: '60% 20%',
          scrub: true,
          // markers: true,
        },
        autoAlpha: 0,
        y: -50,
        duration: 2,
        ease: 'power2.out',
      },
    )
      .fromTo(
        heroButtonResume.current,
        { autoAlpha: 1, y: 0 },
        {
          scrollTrigger: {
            trigger: heroContainer.current,
            start: '60% 45%',
            end: '70% 20%',
            scrub: true,
          },
          autoAlpha: 0,
          y: -80,
          duration: 2,
          ease: 'power2.out',
        },
      )
      .fromTo(
        heroContainerTarget.current,
        { autoAlpha: 0, scale: 0.8, y: 100 },

        {
          scrollTrigger: {
            trigger: heroContainerTarget.current,
            start: '-180 80%',
            end: '10% 50%',
            scrub: true,
            //markers: true,
          },
          scale: 1,
          y: 0,
          duration: 2,
          autoAlpha: 1,
          ease: 'power2.out',
        },
      )
      .fromTo(
        buttonHired.current,
        { autoAlpha: 0, y: 50 },

        {
          scrollTrigger: {
            trigger: heroContainerTarget.current,
            start: '10% 80%',
            end: '20% 50%',
            scrub: true,
            //markers: true,
          },
          y: 0,
          duration: 2,
          autoAlpha: 1,
          ease: 'power2.out',
        },
      );
  }, [isDesktop, isMobile, tl]);

  useEffect(() => {
    if (!isMobile) {
      window.addEventListener('scroll', handleWindowScroll);

      return () => {
        window.removeEventListener('scroll', handleWindowScroll);
      };
    }

    return () => {};
  }, [isMobile]);

  useEffect(() => {
    window.addEventListener('scroll', handleHeroVideoAutoMute);
    return () => {
      window.removeEventListener('scroll', handleHeroVideoAutoMute);
    };
  }, []);

  return (
    <Layout>
      <Seo title="Home" />
      <div style={{ overflow: 'hidden' }}>
        <section className="heroIndexMain" ref={heroContainer} id={'Main'}>
          <div className="container" ref={heroContainerBlock}>
            <div className="flex-row align-items-center">
              <div className="image-container-main">
                <button
                  ref={heroButtonResume}
                  className=" button button-video-resume">
                  <img src="/img/plus-circle.svg" alt="" />
                  Create video resume
                </button>
                <span ref={heroSquare} className="hero-video-square" />
                <div className="hero-video-area" ref={heroVideo}>
                  <div>
                    <img src="/img/nova-thumb.jpg" alt="" />
                    <video
                      ref={vidRef}
                      src="/video/nova.mp4"
                      autoPlay
                      muted
                      loop
                      playsInline></video>
                    <button
                      type="button"
                      role="button"
                      tabIndex={0}
                      className="mute"
                      onClick={handleUnmuteVideo}>
                      <img
                        src="/img/mute.svg"
                        alt=""
                        className={`mute-button ${
                          unmute === true ? `show` : ``
                        }`}
                      />
                      <img
                        src="/img/unmute.svg"
                        alt=""
                        className={`mute-button ${unmute ? `` : `show`}`}
                      />
                    </button>
                  </div>
                  <button
                    ref={buttonHired}
                    className=" button button-video-resume get-hired-button">
                    <img src="/img/plus-circle.svg" alt="" />
                    Get Hired
                  </button>
                </div>
              </div>
              <div className="main-heading col col-4 offset-custom-2">
                <h1>
                  Be Seen,
                  <span> Get Hired</span>
                </h1>
                <p>
                  Cliphire enables jobseekers to easily create and share short
                  video clips with prospective employers.
                </p>
                <Link to="/" className="button button-purple">
                  Get Started for Free
                </Link>
              </div>
            </div>
          </div>
          <svg className="hero-svg-storke" fill="none" viewBox="0 0 1569 502">
            <path
              id="svgPath"
              stroke="url(#paint0_linear)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="91"
              d="M46 49.393C199.741 4.707 241.889 416.145 424.668 416.145s260.25-258.062 474.225-243.602c213.977 14.459 590.717 206.776 623.977 206.776"
              opacity=".398"
            />
            <defs>
              <linearGradient
                id="paint0_linear"
                x1="913.545"
                x2="742.536"
                y1="149.715"
                y2="578.1"
                gradientUnits="userSpaceOnUse">
                <stop stopColor="#FFE8F6" />
                <stop offset="1" stopColor="#E6E5FF" />
              </linearGradient>
            </defs>
          </svg>
        </section>
        <section
          className="indexIntro position-relative"
          ref={secondSection}
          id={'Intro'}>
          <span className="big-gradient-circle delay-1 gradient-anim" />
          <span
            className="big-gradient-circle green-gradient gradient-anim"
            style={{ animationDirection: 'reverse' }}
          />
          <div className="container" ref={heroContainerTarget}>
            <div className="intro-block">
              <div className="intro-head flex-row align-items-center no-margin">
                <img
                  src="/img/cliphire-logo.svg"
                  alt=""
                  className="intro-logo"
                />
                <Link to="/" className="button button-green">
                  Share profile
                </Link>
              </div>
              <div className="intro-image-main flex-row">
                <div className="image-container-intro col col-5">
                  <img src="/img/cliphire-main.jpg" alt="" />
                </div>
                <div className="intro-contents-wrapper col col-5 offset-1">
                  <div className="circle" />
                  <div className="intro-contents">
                    <div className="accordian-1 introduction">
                      <h5>Introduction</h5>
                      <span />
                      <span />
                      <span />
                    </div>
                    <div className="accordian-1 education">
                      <h5>Education</h5>
                    </div>
                    <div className="accordian-1 skill-sets">
                      <h5>Skill Sets</h5>
                    </div>
                    <div className="accordian-1 job-experience">
                      <h5>Job Experience</h5>
                    </div>
                    <div className="accordian-1 expected-salary">
                      <h5>Expected Salary</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <FeaturedJobs/>
        <section className="indexHow" id="how-it-works" ref={howItWorks}>
          <div className="container">
            <h5 className="text-center yellow-heading">How it works</h5>
            <h3 className="text-center">Getting hired has never been easier</h3>
            <div className="how-tabs flex-row no-margin">
              <div className="tab-1 ">
                <div className="tab-image-wrapper">
                  <img src="/img/create-video.svg" alt="" />
                </div>
                <div className="tab-content">
                  <h5>Create Video</h5>
                  <p>
                    Record your introduction video to showcase what you bring to
                    the table.
                  </p>
                </div>
              </div>
              <div className="tab-1 ">
                <div className="tab-image-wrapper">
                  <img src="/img/share-link.svg" alt="" />
                </div>
                <div className="tab-content">
                  <h5>Share Profile</h5>
                  <p>
                    Upload your resume & share your profile with recruiters when
                    applying to jobs.
                  </p>
                </div>
              </div>
              <div className="tab-1 ">
                <div className="tab-image-wrapper">
                  <img src="/img/get-hired.svg" alt="" />
                </div>
                <div className="tab-content">
                  <h5>Get Hired</h5>
                  <p>
                    Get hired faster by standing out to recruiters and hiring
                    managers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <VideoSlider id="Why-Videos" />
        <Feature />
        <Table id={'Competitive-Landscape'} />
        <Testimonial id={'Testimonial'} />
      </div>
    </Layout>
  );
};

export default Index;
